import React, { useEffect, useState } from "react";
import { Alert, Card, CardBody, Col, Container, Row, Spinner } from "reactstrap";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { logoutUser } from "../../../slices/thunks";
import {
  getSubscription,
  Subscription,
} from "../../../helpers/subscription_helper";

/**
 * Soft-lock billing (DOC 09 · LAC-197): assinatura suspended vs canceled.
 * Copy distinto — pausa por pagamento ≠ encerramento definitivo.
 */
const SuspendedBillingPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getSubscription();
        if (!cancelled) {
          setSubscription(res.data ?? null);
        }
      } catch {
        if (!cancelled) {
          setSubscription(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const status = subscription?.status;
  const isSuspended = status === "suspended";
  const isCanceled = status === "canceled";
  // Fallback: se ainda loading ou status desconhecido, copy genérico de acesso limitado.
  const titleKey = isSuspended
    ? "billing.softLock.suspended.title"
    : isCanceled
      ? "billing.softLock.canceled.title"
      : "billing.suspended.title";
  const descriptionKey = isSuspended
    ? "billing.softLock.suspended.description"
    : isCanceled
      ? "billing.softLock.canceled.description"
      : "billing.suspended.description";
  const hintKey = isSuspended
    ? "billing.softLock.suspended.hint"
    : isCanceled
      ? "billing.softLock.canceled.hint"
      : "billing.suspended.hint";

  document.title = t("billing.suspended.documentTitle");

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="justify-content-center">
          <Col lg={7} md={9}>
            <Card className="mt-4">
              <CardBody className="text-center py-5 px-4">
                {loading ? (
                  <Spinner color="primary" />
                ) : (
                  <>
                    <div className="avatar-md mx-auto mb-3">
                      <div
                        className={`avatar-title rounded-circle fs-2 ${
                          isSuspended
                            ? "bg-warning-subtle text-warning"
                            : "bg-danger-subtle text-danger"
                        }`}
                      >
                        <i
                          className={
                            isSuspended
                              ? "ri-pause-circle-line"
                              : "ri-forbid-line"
                          }
                        />
                      </div>
                    </div>
                    <h4 className="mb-2">{t(titleKey)}</h4>
                    <p className="text-muted mb-3">{t(descriptionKey)}</p>
                    <Alert color="light" className="border text-start mb-4">
                      {t(hintKey)}
                    </Alert>
                    <div className="d-flex flex-wrap justify-content-center gap-2">
                      <Link
                        to="/settings/subscription?focus=payment"
                        className="btn btn-primary"
                      >
                        {t("billing.softLock.ctaPlan")}
                      </Link>
                      <button
                        type="button"
                        className="btn btn-soft-secondary"
                        onClick={() => dispatch(logoutUser())}
                      >
                        {t("billing.suspended.logout")}
                      </button>
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default SuspendedBillingPage;
